'use client';

import { c as _c } from "react/compiler-runtime";
import { useTranslations } from 'next-intl';
import type { ReactNode } from 'react';
import { OptimizedImage } from '@/components/images/optimized-image';
import { AppTooltip } from '@/components/ui/app-tooltip';
import { Icon, type IconSvgElement } from '@/components/ui/icon';
import { capitalizeLeading, resolveProposalCategory } from '@/config/proposal-display';
import { useOutcomeColor } from '@/hooks/chart/use-outcome-color';
import type { ProposalOutcomeItem } from './types';
export interface ProposalOutcomeCountProps {
  /** Pubkey of the parent proposal — used as the OptimizedImage entity key for outcome thumbnails. */
  proposalPubkey: string;
  category: string;
  outcomes: ProposalOutcomeItem[];
  categoryIcon?: IconSvgElement;
}
export function ProposalOutcomeCount(t0) {
  const $ = _c(42);
  const {
    proposalPubkey,
    category,
    outcomes,
    categoryIcon
  } = t0;
  const t = useTranslations("launchpad.feed");
  const tCategories = useTranslations("categories");
  let t1;
  if ($[0] !== category || $[1] !== tCategories) {
    const resolved = resolveProposalCategory(category);
    t1 = resolved !== null ? tCategories(resolved.id as Parameters<typeof tCategories>[0]) : capitalizeLeading(category);
    $[0] = category;
    $[1] = tCategories;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const categoryLabel = t1;
  const t2 = outcomes.length > 0 ? 0 : -1;
  let t3;
  if ($[3] !== outcomes.length || $[4] !== t) {
    t3 = t("outcomeCount", {
      count: outcomes.length
    });
    $[3] = outcomes.length;
    $[4] = t;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== t2 || $[7] !== t3) {
    t4 = <span className="text-caption text-foreground-tertiary outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand rounded-item" tabIndex={t2} data-testid="proposal-outcome-count-trigger">{t3}</span>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const countNode = t4;
  let t5;
  if ($[9] !== outcomes) {
    t5 = outcomes.map(_temp);
    $[9] = outcomes;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== proposalPubkey) {
    t6 = {
      marketPubkey: proposalPubkey
    };
    $[11] = proposalPubkey;
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  const colorClasses = useOutcomeColor(t5, t6);
  let countElement = countNode;
  if (outcomes.length > 0) {
    let t7;
    if ($[13] !== t) {
      t7 = t("outcomesTooltipTitle");
      $[13] = t;
      $[14] = t7;
    } else {
      t7 = $[14];
    }
    let t8;
    if ($[15] !== t7) {
      t8 = <span className="text-caption font-medium opacity-70">{t7}</span>;
      $[15] = t7;
      $[16] = t8;
    } else {
      t8 = $[16];
    }
    let t9;
    if ($[17] !== colorClasses || $[18] !== outcomes || $[19] !== proposalPubkey) {
      let t10;
      if ($[21] !== colorClasses || $[22] !== proposalPubkey) {
        t10 = (outcome, idx) => {
          const hue = colorClasses[idx]?.hue ?? "blue";
          const resolvedImageUrl = outcome.imageNsfw === false && outcome.imageUrl !== "" && outcome.imageUrl !== undefined ? outcome.imageUrl : null;
          return <li key={`${String(outcome.outcomeIndex)}-${outcome.label}`} className="flex items-center gap-2"><OptimizedImage variant="thumbnail" entity={{
              kind: "outcome",
              marketPubkey: proposalPubkey,
              outcomeIndex: outcome.outcomeIndex,
              hue,
              imageUrl: resolvedImageUrl,
              nsfw: outcome.imageNsfw
            }} alt="" size="size-4" rounded="rounded-full" preview={false} /><span>{outcome.label}</span></li>;
        };
        $[21] = colorClasses;
        $[22] = proposalPubkey;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      t9 = outcomes.map(t10);
      $[17] = colorClasses;
      $[18] = outcomes;
      $[19] = proposalPubkey;
      $[20] = t9;
    } else {
      t9 = $[20];
    }
    let t10;
    if ($[24] !== t9) {
      t10 = <ul className="flex flex-col gap-1.5">{t9}</ul>;
      $[24] = t9;
      $[25] = t10;
    } else {
      t10 = $[25];
    }
    let t11;
    if ($[26] !== t10 || $[27] !== t8) {
      t11 = <div className="flex flex-col gap-1.5">{t8}{t10}</div>;
      $[26] = t10;
      $[27] = t8;
      $[28] = t11;
    } else {
      t11 = $[28];
    }
    const tooltipContent = t11;
    let t12;
    if ($[29] !== countNode || $[30] !== tooltipContent) {
      t12 = <AppTooltip content={tooltipContent} contentClassName="py-2.5">{countNode}</AppTooltip>;
      $[29] = countNode;
      $[30] = tooltipContent;
      $[31] = t12;
    } else {
      t12 = $[31];
    }
    countElement = t12;
  }
  let t7;
  if ($[32] !== categoryIcon) {
    t7 = categoryIcon ? <Icon icon={categoryIcon} aria-hidden={true} className="size-3.5 shrink-0" /> : null;
    $[32] = categoryIcon;
    $[33] = t7;
  } else {
    t7 = $[33];
  }
  let t8;
  if ($[34] !== categoryLabel) {
    t8 = <span className="truncate">{categoryLabel}</span>;
    $[34] = categoryLabel;
    $[35] = t8;
  } else {
    t8 = $[35];
  }
  let t9;
  if ($[36] !== t7 || $[37] !== t8) {
    t9 = <span className="flex min-w-0 items-center gap-1 text-body font-medium text-foreground-primary">{t7}{t8}</span>;
    $[36] = t7;
    $[37] = t8;
    $[38] = t9;
  } else {
    t9 = $[38];
  }
  let t10;
  if ($[39] !== countElement || $[40] !== t9) {
    t10 = <div className="flex flex-col gap-0.5 leading-tight min-w-0">{t9}{countElement}</div>;
    $[39] = countElement;
    $[40] = t9;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}
function _temp(o) {
  return {
    title: o.label,
    index: o.outcomeIndex
  };
}